/**
 * CentroControlAdmin — Centro de control runtime.
 *
 * Ruta: /admin/centro-de-control
 *
 * Mapa unificado de TODO lo que se puede gestionar desde el admin sin tocar
 * código ni esperar un deploy. Cada item dice:
 *   - qué controla
 *   - dónde se gestiona (deep link)
 *   - estado: 🟢 Runtime · 🟠 Mixto · 🔴 Deploy-bound (todavía)
 *
 * El bloque final "Aún requiere deploy" lista los env vars que todavía no
 * tienen UI — sirve de roadmap visible para migrar progresivamente.
 *
 * Esta página NO duplica la edición — solo concentra la NAVEGACIÓN. Para
 * editar cada cosa, click → te lleva a su page dedicado.
 */

import { Link } from "react-router-dom";
import {
  Globe,
  Video,
  Gift,
  Megaphone,
  Layout,
  Image as ImageIcon,
  Package,
  Repeat2,
  Mail,
  Shield,
  Flag,
  Zap,
  Plug,
  HeartPulse,
  Filter,
  Activity,
  PlayCircle,
  Sparkles,
  AlertTriangle,
  ArrowUpRight,
  SlidersHorizontal,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Badge } from "@/components/ui/badge";
import {
  CONFIG_PIPELINE_MATRIX,
  CONFIG_PIPELINE_DUAL_CTA,
  CONFIG_PIPELINE_EDGE_WISHLIST,
  DUAL_CTA_FREE_SHOW_PRIORITY,
  DUAL_CTA_FREE_URL_PRIORITY,
  DUAL_CTA_VIP_CHARGE_PRIORITY,
} from "@/lib/configPipelines";

type Status = "runtime" | "mixed" | "deploy_bound";

type ControlItem = {
  title: string;
  description: string;
  to?: string;
  icon: LucideIcon;
  status: Status;
  /** Si la edición vive en otra page, el label del botón ("Editar", "Gestionar"…). */
  actionLabel?: string;
  /** Hint del backing store. Ej. "site_settings.international_home_template_by_country". */
  storage?: string;
};

type ControlGroup = {
  title: string;
  description: string;
  items: ControlItem[];
};

/**
 * Groups mirror sidebar IA (Resumen / Operación / Conversión / Sitio / Comercio / Sistema).
 * Each item deep-links to the page that owns that config — never edit here.
 */
const GROUPS: ControlGroup[] = [
  {
    title: "Conversión · Home DualCTA",
    description:
      "Qué plantilla, video Mux y precio VIP ve cada país en `/`. Dueño: Home por país. Postgres no-store.",
    items: [
      {
        title: "Plantilla DualCTA + free on/off + VIP Price Stripe",
        description:
          "ISO2 → DualCTAVideoLanding, Mux playback, canal gratis DualCTA, VIP { fullPriceUsd, stripePriceId, promo }. Sin stripePriceId cae a fallback FIRSTANA — no a Suscripciones.",
        to: "/admin/country-templates",
        icon: Globe,
        status: "runtime",
        actionLabel: "Home por país",
        storage:
          "site_settings: international_home_*_by_country, mux_by_ar_geo, vip_pricing_by_country, dual_cta_free_telegram_*",
      },
      {
        title: "Mux multi-video por país (A/B)",
        description:
          "Varios playback IDs por ISO2 con label, key y peso. Sticky A/B; medir en PostHog con mux_variant_key / mux_variant_label.",
        to: "/admin/country-templates",
        icon: PlayCircle,
        status: "runtime",
        actionLabel: "Mux A/B por país",
        storage:
          "site_settings.international_home_mux_by_country → string | { variants[] }",
      },
      {
        title: "Mux AR por provincia / ciudad",
        description:
          "Video distinto por provincia o ciudad AR. Prioridad: ciudad → provincia → país AR → global.",
        to: "/admin/country-templates",
        icon: PlayCircle,
        status: "runtime",
        actionLabel: "Mux geo AR",
        storage: "site_settings.international_home_mux_by_ar_geo",
      },
      {
        title: "VIP checkout (Embedded drawer)",
        description:
          "Cobro DualCTA + /invitacion-exclusive: 1) país stripePriceId+promo 2) vipPricingDefault compact 3) membership_products.vip 4) FALLBACK FIRSTANA. SPA y server usan la misma cadena.",
        to: "/admin/country-templates",
        icon: Repeat2,
        status: "runtime",
        actionLabel: "Price por país",
        storage:
          "vip_pricing_by_country | vipPricingDefault → resolveVipChargeConfig → Stripe",
      },
      {
        title: "/invitacion-exclusive (Mux 1 CTA)",
        description:
          "Ruta canónica InvitacionExclusivaMux. Mismo VIP admin que DualCTA. Worker ≥2.6.7 debe inyectar InvitacionExclusivaMux (no DualCTA) y nunca hideCta.",
        to: "/invitacion-exclusive",
        icon: PlayCircle,
        status: "runtime",
        actionLabel: "Abrir exclusive",
        storage: "SPA route + site_settings VIP + Worker path override",
      },
    ],
  },
  {
    title: "Conversión · CTAs geo / overrides",
    description:
      "Distinto de DualCTA home. CTA por región = wishlist/cumpleaños; CTAs = overrides infra; kill switch hero en wishlist.",
    items: [
      {
        title: "CTA por región AR (Exclusivo → /invitacion-exclusive)",
        description:
          "AR home = BaseTemplate. Wishlist cta_type community = botón «Exclusivo» → /invitacion-exclusive (Mux VIP). birthday = Comunidad; none = sin CTA. Intl no usa esto: DualCTA en `/`.",
        to: "/admin/wishlist-config",
        icon: Gift,
        status: "runtime",
        actionLabel: "CTA por región",
        storage: "wishlist_geo_config (community → exclusive URL)",
      },
      {
        title: "Kill switch hero CTAs (Mux VIP + free)",
        description:
          "hide_global_hero_cta oculta ambos botones del overlay DualCTA / Mux. Vive en la misma page de CTA por región.",
        to: "/admin/wishlist-config",
        icon: Filter,
        status: "runtime",
        actionLabel: "Hero CTA global",
        storage: "site_settings.hide_global_hero_cta",
      },
      {
        title: "CTAs y overrides manuales",
        description:
          "Defaults env + overrides geo (precio/URL/toggle) con cache KV. No es DualCTA VIP Price.",
        to: "/admin/cta",
        icon: Megaphone,
        status: "runtime",
        actionLabel: "CTAs overrides",
        storage: "cta_overrides",
      },
      {
        title: "Free Telegram (URL / master / label)",
        description:
          "URLs free + master free_telegram_enabled. DualCTA free show/hide también en Home por país.",
        to: "/admin/premium-invite-config",
        icon: PlayCircle,
        status: "runtime",
        actionLabel: "Invit. premium",
        storage: "premium_invite_config + dual_cta_free_*",
      },
    ],
  },
  {
    title: "Operación · solo lectura",
    description:
      "Mirar funnel, leads y fotos. No se edita config de conversión acá.",
    items: [
      {
        title: "Live funnel",
        description: "Eventos y sesiones en vivo (últimos minutos).",
        to: "/admin/live-funnel",
        icon: Activity,
        status: "runtime",
        actionLabel: "Live",
        storage: "funnel_events",
      },
      {
        title: "Funnel checkout",
        description: "Agregados started → completed → abandoned por template/plan.",
        to: "/admin/checkout-funnel",
        icon: Filter,
        status: "runtime",
        actionLabel: "Funnel",
        storage: "premium_checkout_intents aggregates",
      },
      {
        title: "Leads recovery",
        description:
          "Directorio de emails que intentaron pagar (export CSV). No es stats de conversión.",
        to: "/admin/recovery-leads",
        icon: Mail,
        status: "runtime",
        actionLabel: "Leads",
        storage: "premium_checkout_intents",
      },
      {
        title: "Métricas de fotos",
        description: "Dwell y países de lightbox. Subir fotos → Portfolio.",
        to: "/admin/image-metrics",
        icon: ImageIcon,
        status: "runtime",
        actionLabel: "Fotos",
        storage: "image_views",
      },
    ],
  },
  {
    title: "Sitio",
    description: "Identidad visual y contenido del sitio público.",
    items: [
      {
        title: "Configuración del sitio",
        description:
          "Logo, banner, hero, galería BaseTemplate. No Mux/template por país.",
        to: "/admin/site-settings",
        icon: Layout,
        status: "runtime",
        actionLabel: "Editar",
        storage: "site_settings (live)",
      },
      {
        title: "Portfolio",
        description: "Secciones e imágenes Cloudinary. Métricas → Métricas fotos.",
        to: "/admin/portfolio",
        icon: ImageIcon,
        status: "runtime",
        actionLabel: "Editar",
        storage: "portfolio_sections",
      },
      {
        title: "Audiovisual",
        description: "Videos YouTube / destacados del sitio.",
        to: "/admin/audiovisual",
        icon: Video,
        status: "runtime",
        actionLabel: "Gestionar",
      },
    ],
  },
  {
    title: "Comercio",
    description: "Catálogo, planes, códigos, aprobaciones y links AR.",
    items: [
      {
        title: "Suscripciones (Price IDs catálogo)",
        description:
          "membership_products por plan_tier. DualCTA home prioriza Home por país; sin Price en país → FIRSTANA.",
        to: "/admin/suscripciones",
        icon: Repeat2,
        status: "mixed",
        actionLabel: "Planes",
        storage: "membership_products.stripe_price_id",
      },
      {
        title: "Productos one-time",
        description: "Packs / productos individuales (no memberships).",
        to: "/admin/productos",
        icon: Package,
        status: "runtime",
        actionLabel: "Productos",
      },
      {
        title: "Aprobaciones de membresía",
        description: "Aprobar pending + pegar link Telegram privado.",
        to: "/admin/memberships",
        icon: Shield,
        status: "runtime",
        actionLabel: "Aprobar",
        storage: "user_memberships",
      },
      {
        title: "Códigos de invitación",
        description: "Generar códigos one-time. Config página invite → Invit. premium.",
        to: "/admin/invitaciones",
        icon: Mail,
        status: "runtime",
        actionLabel: "Códigos",
        storage: "invitation_codes",
      },
      {
        title: "Invitación premium / free Telegram",
        description: "Funnels /invitacion-exclusive + free Telegram URLs.",
        to: "/admin/premium-invite-config",
        icon: Sparkles,
        status: "runtime",
        actionLabel: "Config invite",
        storage: "premium_invite_config",
      },
      {
        title: "Links Argentina",
        description: "URLs compartibles AR (Stories/DM) → checkout MP.",
        to: "/admin/argen-links",
        icon: Flag,
        status: "runtime",
        actionLabel: "Links AR",
      },
    ],
  },
  {
    title: "Sistema",
    description: "Diagnóstico, edge y permisos. No hay edición de producto acá.",
    items: [
      {
        title: "Integraciones",
        description: "Ping en vivo: Stripe, MP, DB, Resend, Clerk…",
        to: "/admin/integraciones",
        icon: Plug,
        status: "runtime",
        actionLabel: "Health",
      },
      {
        title: "Config Health",
        description: "Gaps de config por país / template (producto), no pings de API.",
        to: "/admin/config-health",
        icon: HeartPulse,
        status: "runtime",
        actionLabel: "Diagnóstico",
      },
      {
        title: "Worker / Edge",
        description: "Qué inyecta el Cloudflare Worker en prod.",
        to: "/admin/worker-flags",
        icon: Zap,
        status: "runtime",
        actionLabel: "Worker",
        storage: "Worker vars / __TEMPLATE_CONFIG__",
      },
      {
        title: "Env vars",
        description: "Checklist Railway + Worker (deploy-bound).",
        to: "/admin/env-setup",
        icon: SlidersHorizontal,
        status: "deploy_bound",
        actionLabel: "Checklist",
      },
      {
        title: "Sidebar por rol",
        description: "Qué ve admin vs super_admin en el menú.",
        to: "/admin/sidebar-config",
        icon: SlidersHorizontal,
        status: "runtime",
        actionLabel: "Sidebar",
        storage: "sidebar_config",
      },
    ],
  },
];

/**
 * Roadmap: env vars que todavía requieren deploy. Cada uno es candidato a
 * pasar a `premium_invite_config` (o a una tabla nueva) para que admin lo
 * controle sin tocar env vars.
 */
const DEPLOY_BOUND: { name: string; controls: string; nextStep: string }[] = [
  {
    name: "VITE_TALLY_PRIVATE_MSG_URL",
    controls: "URL del formulario Tally en CTA 'Enviar Privado' (DualCTAVideoLanding).",
    nextStep: "Añadir `tally_private_msg_url` a premium_invite_config + admin field.",
  },
  {
    name: "VITE_PREMIUM_USD_VIP_PRICE / VITE_PREMIUM_ARS_VIP_PRICE / VITE_PREMIUM_EUR_VIP_PRICE",
    controls: "Chip de precio que se muestra encima del CTA en DualCTAVideoLanding.",
    nextStep: "Añadir `display_price_by_currency` JSONB a premium_invite_config.",
  },
  {
    name: "VITE_BRAND_INSTAGRAM_URL / TIKTOK_URL / TELEGRAM_URL / YOUTUBE_URL",
    controls: "Handles + URLs sociales del brand (footer + tarjetas de contacto).",
    nextStep: "Añadir `brand_social_links` a site_settings (variant test + publish).",
  },
  {
    name: "VITE_PREMIUM_SPECIAL_OFFER + VITE_SPECIAL_OFFER_PLAYBACK_ID",
    controls: "Toggle del Special Offer (video Mux específico de promoción).",
    nextStep:
      "Añadir `special_offer_active` + `special_offer_playback_id` a premium_invite_config.",
  },
  {
    name: "VITE_GALLERY_PHOTOS",
    controls: "Galería override por env (alternativa a portfolio).",
    nextStep:
      "Ya cubierto por `/admin/portfolio` y `site_settings.galleryImageUrls`. Deprecable.",
  },
];

function StatusBadge({ status }: { status: Status }) {
  if (status === "runtime") {
    return (
      <Badge
        variant="outline"
        className="border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
      >
        Runtime
      </Badge>
    );
  }
  if (status === "mixed") {
    return (
      <Badge
        variant="outline"
        className="border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"
      >
        Mixto
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-400"
    >
      Deploy
    </Badge>
  );
}

function ItemCard({ item }: { item: ControlItem }) {
  const Icon = item.icon;
  const inner = (
    <div className="group flex h-full items-start gap-3 rounded-xl border bg-card p-3.5 transition-colors hover:border-border/80 hover:bg-card/80">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted/60 text-muted-foreground transition-colors group-hover:bg-muted">
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </span>
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex items-start justify-between gap-2">
          <h4 className="truncate text-[13px] font-semibold leading-tight">
            {item.title}
          </h4>
          <StatusBadge status={item.status} />
        </div>
        <p className="text-[11.5px] leading-snug text-muted-foreground">
          {item.description}
        </p>
        {item.storage && (
          <p className="font-mono text-[10px] leading-snug text-muted-foreground/70">
            {item.storage}
          </p>
        )}
        {item.to && (
          <div className="flex items-center gap-1 pt-0.5 text-[11px] font-medium text-foreground/80 group-hover:text-foreground">
            <span>{item.actionLabel ?? "Abrir"}</span>
            <ArrowUpRight className="h-3 w-3" strokeWidth={2} />
          </div>
        )}
      </div>
    </div>
  );

  if (item.to) {
    return <Link to={item.to}>{inner}</Link>;
  }
  return inner;
}

export default function CentroControlAdmin() {
  const runtimeCount = GROUPS.reduce(
    (acc, g) => acc + g.items.filter((i) => i.status === "runtime").length,
    0,
  );
  const totalItems = GROUPS.reduce((acc, g) => acc + g.items.length, 0);

  return (
    <div className="space-y-8">
      <AdminPageHeader
        description="Índice de config runtime — no edita datos. Cada ítem te lleva a la page dueña (misma IA que el sidebar)."
      />

      {/* Resumen rápido */}
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border bg-gradient-to-br from-emerald-50/40 to-card p-4 dark:from-emerald-950/10">
          <div className="text-3xl font-bold leading-none text-emerald-700 dark:text-emerald-400">
            {runtimeCount}
          </div>
          <div className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">
            sin deploy
          </div>
          <p className="mt-1.5 text-[11.5px] text-muted-foreground">
            Configurables desde la UI; cambios aplican en segundos.
          </p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <div className="text-3xl font-bold leading-none">{totalItems}</div>
          <div className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">
            controles totales
          </div>
          <p className="mt-1.5 text-[11.5px] text-muted-foreground">
            Distribuidos en {GROUPS.length} categorías.
          </p>
        </div>
        <div className="rounded-xl border bg-gradient-to-br from-red-50/40 to-card p-4 dark:from-red-950/10">
          <div className="text-3xl font-bold leading-none text-red-700 dark:text-red-400">
            {DEPLOY_BOUND.length}
          </div>
          <div className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">
            requieren deploy
          </div>
          <p className="mt-1.5 text-[11.5px] text-muted-foreground">
            Roadmap visible abajo; candidatos a migrar a runtime.
          </p>
        </div>
      </div>

      {/* Pipelines alineados: A edge wishlist KV · B DualCTA conversion */}
      <section className="rounded-xl border border-sky-500/25 bg-sky-500/[0.05] p-4 space-y-3">
        <div className="flex items-start gap-2">
          <SlidersHorizontal className="mt-0.5 h-4 w-4 shrink-0 text-sky-600 dark:text-sky-400" />
          <div>
            <h2 className="text-sm font-semibold tracking-tight">
              Pipelines de config (orquestación)
            </h2>
            <p className="mt-0.5 text-[11.5px] text-muted-foreground">
              Ambos usan <span className="font-mono">Postgres</span> como source of
              truth. Cache = KV <span className="font-semibold">versionado</span>{" "}
              (nunca TTL ciego). Código:{" "}
              <span className="font-mono">src/lib/configPipelines.ts</span>
            </p>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 text-[11.5px] leading-snug">
          {([CONFIG_PIPELINE_EDGE_WISHLIST, CONFIG_PIPELINE_DUAL_CTA] as const).map(
            (id) => {
              const p = CONFIG_PIPELINE_MATRIX[id];
              return (
                <div
                  key={id}
                  className="rounded-lg border bg-card/80 p-3 space-y-1.5"
                >
                  <p className="font-semibold text-[12px]">{p.title}</p>
                  <p className="text-muted-foreground">
                    <span className="text-foreground">Lee:</span> {p.consumer}
                  </p>
                  <p className="text-muted-foreground">
                    <span className="text-foreground">Cache:</span> {p.cache}
                  </p>
                  <p className="font-mono text-[10px] text-muted-foreground/80">
                    {p.admin.join(" · ")}
                  </p>
                  <p className="text-[10px] text-muted-foreground">{p.maxStaleHint}</p>
                </div>
              );
            },
          )}
        </div>
      </section>

      {/* Cheat sheet DualCTA: prioridad free + VIP */}
      <section className="rounded-xl border border-amber-500/25 bg-amber-500/[0.06] p-4 space-y-3">
        <div className="flex items-start gap-2">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <div>
            <h2 className="text-sm font-semibold tracking-tight">
              DualCTA home — prioridad de conversión
            </h2>
            <p className="mt-0.5 text-[11.5px] text-muted-foreground">
              Pipeline B. Postgres + no-store + dual_cta_public KV versionado (TTL
              corto). Cliente poll ≤20s. Cambios admin: hard reload o foco de
              pestaña.
            </p>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 text-[11.5px] leading-snug">
          <div className="rounded-lg border bg-card/80 p-3 space-y-1.5">
            <p className="font-semibold text-[12px]">Click gratis (Telegram)</p>
            <p className="text-[10px] font-medium text-muted-foreground">Show/hide</p>
            <ol className="list-decimal pl-4 space-y-0.5 text-muted-foreground">
              {DUAL_CTA_FREE_SHOW_PRIORITY.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ol>
            <p className="text-[10px] font-medium text-muted-foreground pt-1">URL</p>
            <ol className="list-decimal pl-4 space-y-0.5 text-muted-foreground">
              {DUAL_CTA_FREE_URL_PRIORITY.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ol>
            <p className="pt-1 font-mono text-[10px] text-muted-foreground/80">
              Evento dual_cta_video_free_click · /admin/premium-invite-config +
              country-templates
            </p>
          </div>
          <div className="rounded-lg border bg-card/80 p-3 space-y-1.5">
            <p className="font-semibold text-[12px]">VIP / Membresía</p>
            <p className="text-[10px] font-medium text-muted-foreground">Cobro</p>
            <ol className="list-decimal pl-4 space-y-0.5 text-muted-foreground">
              {DUAL_CTA_VIP_CHARGE_PRIORITY.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ol>
            <ul className="list-disc pl-4 space-y-0.5 text-muted-foreground pt-1">
              <li>
                Checkout: Stripe <span className="text-foreground">Embedded</span>{" "}
                → fallback hosted
              </li>
              <li>
                Sin country Price:{" "}
                <span className="text-foreground">membership_products</span> (no
                FIRSTANA forzado desde el cliente)
              </li>
              <li>
                Evento{" "}
                <span className="font-mono">dual_cta_video_premium_click</span>
              </li>
            </ul>
            <p className="pt-1 font-mono text-[10px] text-muted-foreground/80">
              Price por país: country-templates · catálogo: /admin/suscripciones
            </p>
          </div>
        </div>
      </section>

      {/* Grupos de controles runtime */}
      {GROUPS.map((group) => (
        <section key={group.title} className="space-y-3">
          <div className="flex items-baseline justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold tracking-tight">
                {group.title}
              </h2>
              <p className="text-xs text-muted-foreground">{group.description}</p>
            </div>
            <span className="text-[11px] text-muted-foreground">
              {group.items.length} {group.items.length === 1 ? "control" : "controles"}
            </span>
          </div>
          <div className="grid gap-2.5 sm:grid-cols-2">
            {group.items.map((item) => (
              <ItemCard key={item.title} item={item} />
            ))}
          </div>
        </section>
      ))}

      {/* Deploy-bound: roadmap */}
      <section className="space-y-3">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold tracking-tight">
              <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
              Aún requieren deploy
            </h2>
            <p className="text-xs text-muted-foreground">
              Env vars que cambiar implica un push + redeploy en Railway. Roadmap
              para migrar a runtime — abrí un issue al sumar uno nuevo.
            </p>
          </div>
        </div>
        <div className="rounded-xl border border-red-500/20 bg-red-50/30 p-4 dark:bg-red-950/10">
          <div className="space-y-3">
            {DEPLOY_BOUND.map((item) => (
              <div
                key={item.name}
                className="space-y-1 border-b border-red-500/10 pb-3 last:border-b-0 last:pb-0"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <code className="rounded bg-red-500/10 px-1.5 py-0.5 font-mono text-[11px] text-red-700 dark:text-red-400">
                    {item.name}
                  </code>
                  <Badge variant="outline" className="border-red-500/30 text-[10px] text-red-700 dark:text-red-400">
                    Deploy
                  </Badge>
                </div>
                <p className="text-[12px] leading-snug text-foreground/80">
                  {item.controls}
                </p>
                <p className="flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground">
                  <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-amber-500" />
                  <span>
                    <span className="font-medium text-foreground/80">Próximo paso:</span>{" "}
                    {item.nextStep}
                  </span>
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
